import { Link } from 'react-router-dom';
import type { Cell } from '../demo/seed';
import { GliderLogo } from './GliderLogo';
import { labelForTier, colorForTier } from '../lib/survival';
import { fmtTon, fmtCellId } from '../lib/format';
import { Sparkline } from './Sparkline';

type Props = {
  rank: number;
  cell: Cell;
  primaryStat: string;
  primaryLabel: string;
};

export function LeaderboardRow({ rank, cell, primaryStat, primaryLabel }: Props) {
  if (!cell.agent) return null;
  return (
    <Link
      to={`/lineage/${cell.id}`}
      className="grid grid-cols-[40px_28px_1fr_88px_120px_140px_60px] sm:grid-cols-[48px_32px_1fr_120px_140px_180px_80px] items-center gap-3 py-2 px-3 hairline bg-panel hover:bg-void"
    >
      <span className="font-mono text-text-mono-dim text-sm">#{rank.toString().padStart(3, '0')}</span>
      <GliderLogo size={20} />
      <div className="min-w-0">
        <div className="truncate text-text-primary">{cell.agent.name}</div>
        <div className="text-2xs text-text-mono-dim font-mono hidden sm:block">{fmtCellId(cell.id)}</div>
      </div>
      <span
        className="text-2xs font-mono uppercase tracking-tightest hidden sm:inline-block"
        style={{ color: colorForTier(cell.state) }}
      >
        {labelForTier(cell.state)}
      </span>
      <span className="font-mono tabular text-text-primary">
        <span className="text-text-mono-dim text-2xs block sm:inline">{primaryLabel} </span>
        {primaryStat}
      </span>
      <div className="hidden sm:block">
        <Sparkline values={cell.agent.sparkline} width={160} height={28} />
      </div>
      <span className="text-blue-life text-2xs justify-self-end">View →</span>
    </Link>
  );
}

export function LeaderboardHeader() {
  return (
    <div className="hidden sm:grid grid-cols-[48px_32px_1fr_120px_140px_180px_80px] items-center gap-3 py-2 px-3 text-2xs uppercase tracking-tightest text-text-mono-dim border-b border-grid-line">
      <span>Rank</span>
      <span />
      <span>Agent</span>
      <span>State</span>
      <span>Stat</span>
      <span>24h</span>
      <span />
    </div>
  );
}

export function fmtPrimary(cell: Cell, mode: 'rich' | 'earn' | 'survive' | 'blood'): { value: string; label: string } {
  if (!cell.agent) return { value: '–', label: '' };
  switch (mode) {
    case 'rich':
      return { value: `◊ ${fmtTon(cell.agent.balance)}`, label: 'BAL' };
    case 'earn':
      return { value: `◊ ${fmtTon(cell.agent.revenue24h)}`, label: '24h' };
    case 'survive': {
      const days = (Date.now() - cell.agent.bornAt) / 86_400_000;
      return { value: `${days.toFixed(1)}d`, label: 'ALIVE' };
    }
    case 'blood':
      return { value: `${cell.agent.childrenIds.length}`, label: 'KIDS' };
  }
}
